import Pill from 'common/components/pills/pill';
import { PillTheme } from 'common/components/pills/pill-theme';
import { DataScienceTaskStatus } from '../tasks-status';
import {
  useTriggerTaskRunMutation,
  useUpdateDataScienceTaskMutation
} from 'app/apis/commandApi';
import {
  createTriggerCacheKey,
  createUpdateCacheKey
} from '../task-cache-keys';
import { RootState } from 'app/store';
import { useSelector } from 'react-redux';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import Spinner, { SpinnerSize } from 'common/components/loading/spinner';
import { DataScienceTaskFailureInformation } from '../tasks.models';

const getStatusPillTheme = (status: DataScienceTaskStatus): PillTheme => {
  switch (status) {
    case DataScienceTaskStatus.COMPLETED:
      return PillTheme.SUCCESS;
    case DataScienceTaskStatus.PROCESSING:
    case DataScienceTaskStatus.MODELAPPROVED:
      return PillTheme.PROCESSING;
    case DataScienceTaskStatus.FAILED:
      return PillTheme.ERROR;
    case DataScienceTaskStatus.DISABLED:
      return PillTheme.DISABLED;
    case DataScienceTaskStatus.SCHEDULED:
      return PillTheme.SCHEDULED;
    case DataScienceTaskStatus.STAGING:
      return PillTheme.WAITING;
    default:
      throw new Error(
        `Unable to get pill theme from Data science task Status: ${status}`
      );
  }
};

interface DataScienceTaskTableStatusProps {
  currentStatus: DataScienceTaskStatus;
  machineLearningModel: string;
  dataScienceTaskFailureInformation?: DataScienceTaskFailureInformation;
}

export const DataScienceTasksTableStatus = ({
  currentStatus,
  machineLearningModel,
  dataScienceTaskFailureInformation
}: DataScienceTaskTableStatusProps) => {
  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const theme = getStatusPillTheme(currentStatus);

  const [_, triggerTaskRunMetaData] = useTriggerTaskRunMutation({
    fixedCacheKey: createTriggerCacheKey(machineLearningModel, siteId)
  });

  const [__, updateDataScienceTaskMetadata] = useUpdateDataScienceTaskMutation({
    fixedCacheKey: createUpdateCacheKey(machineLearningModel, siteId)
  });

  const renderCell = () => {
    if (triggerTaskRunMetaData.status === QueryStatus.pending) {
      return (
        <div className="flex items-center">
          <Spinner size={SpinnerSize.SMALL} />
          <span className="ml-2 text-xs leading-6">Trigger Task...</span>
        </div>
      );
    } else if (updateDataScienceTaskMetadata.status === QueryStatus.pending) {
      return (
        <div className="flex items-center">
          <Spinner size={SpinnerSize.SMALL} />
          <span className="ml-2 text-xs leading-6">Updating Task...</span>
        </div>
      );
    } else {
      return (
        <div className="w-28 text-center">
          <Pill
            theme={theme}
            tooltipText={`Step: ${dataScienceTaskFailureInformation?.stepName} Failed. \n${dataScienceTaskFailureInformation?.failureReason}`}
          >
            <span className="text-sm leading-6">{currentStatus} </span>
          </Pill>
        </div>
      );
    }
  };
  return (
    <div className="flex items-center justify-between relative">
      {renderCell()}
    </div>
  );
};

export default DataScienceTasksTableStatus;
